import { I18nAndL10n } from '@application/i18n/mod';
import { withTranslation } from '@application/i18n/with_translation';
import { DateDisplayFormats, DateService } from '@application/time/date.service';
import { HeadlessButton, IconButton, VisuallyHidden } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { useResolve } from '@visiba/instantiation';
import { useConstant } from '@visiba/react-std';
import type { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

interface WeeklyCalenderProps {
	date?: Dayjs;
	fromDate?: Dayjs;
	toDate?: Dayjs;
	onDateChange?: (date: Dayjs) => void;
	onDayClick?: (date: Dayjs) => void;
	notifications?: Map<string, number>;
}

function getDateSpanDisplay(today: Dayjs, startOfCurrentWeek: Dayjs, endOfCurrentWeek: Dayjs) {
	// If the current week is the same as the current year, we only show the day
	// and month. Otherwise, we includes the year too.
	const dateFormat =
		today.isSame(startOfCurrentWeek, 'year') && today.isSame(endOfCurrentWeek, 'year')
			? DateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_CURRENT_YEAR_WEEKDAY_TIME)
			: DateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_TIME_WEEKDAY);

	return `${startOfCurrentWeek.format(dateFormat)} - ${endOfCurrentWeek.format(dateFormat)}`;
}

export function WeeklyCalender({ date, fromDate, toDate, notifications, onDateChange, onDayClick }: WeeklyCalenderProps): JSX.Element {
	const dateService = useResolve(DateService);
	const [currentDate, setCurrentDate] = useState(date ?? dateService.newDate());

	const today = useConstant(() => fromDate ?? dateService.newDate());
	const startOfTodayWeek = useConstant(() => today.startOf('week'));

	function getFirstAvailableDayOfWeek(date: Dayjs) {
		if (date.isBefore(today)) {
			return today.clone();
		}

		return date.startOf('week');
	}

	const startOfCurrentWeek = currentDate.startOf('week');
	const endOfCurrentWeek = currentDate.endOf('week');
	const prevWeek = currentDate.subtract(1, 'week').startOf('week');
	const disablePrevButton = prevWeek.isBefore(startOfTodayWeek);
	const disableNextButton = toDate ? toDate.isBefore(endOfCurrentWeek) : false;

	const currentWeekText = withTranslation('Component.WeeklyCalendar.LabelCurrentWeek', {
		week: endOfCurrentWeek.week().toString(),
	});

	useEffect(
		function whenDateChanges() {
			setCurrentDate(date ?? dateService.newDate());
		},
		[date, dateService],
	);

	function handlePrevWeek() {
		onDateChange?.(getFirstAvailableDayOfWeek(prevWeek));
		setCurrentDate(getFirstAvailableDayOfWeek(prevWeek));
	}

	function handleNextWeek() {
		const nextWeek = getFirstAvailableDayOfWeek(currentDate.add(1, 'week').startOf('week'));

		onDateChange?.(nextWeek);
		setCurrentDate(nextWeek);
	}

	function handleDayClick(date: Dayjs) {
		onDayClick?.(date);
	}

	const dayList = [];

	for (let day = startOfCurrentWeek.clone(); day.isBefore(endOfCurrentWeek); day = day.add(1, 'day')) {
		dayList.push({
			date: day.clone(),
			disabled: day.isBefore(today, 'day') || (toDate && day.isAfter(toDate.startOf('day'))),
			notification: notifications?.get(day.format('YYYY-MM-DD')) ?? false,
		});
	}

	return (
		<elements.container aria-label={currentWeekText} tabIndex={0} role='region'>
			<elements.currentDateContainer>
				<IconButton
					icon='chevronLeft'
					onClick={handlePrevWeek}
					disabled={disablePrevButton}
					variant='interactive'
					size='medium'
					text={I18nAndL10n.translate('Component.WeeklyCalendar.PreviousWeek')}
				/>
				<elements.currentDate aria-live='polite' aria-relevant='all'>
					<VisuallyHidden>{currentWeekText}</VisuallyHidden>
					{getDateSpanDisplay(today, startOfCurrentWeek, endOfCurrentWeek)}
				</elements.currentDate>
				<IconButton
					icon='chevronRight'
					onClick={handleNextWeek}
					disabled={disableNextButton}
					variant='interactive'
					size='medium'
					text={I18nAndL10n.translate('Component.WeeklyCalendar.NextWeek')}
				/>
			</elements.currentDateContainer>
			<elements.days>
				{dayList.map((dayButton) => {
					const ariaLabel = `${dayButton.date.format(
						dateService.getLocaleSpecificDateFormatTemplate(DateDisplayFormats.FULL_DISPLAY_NO_TIME),
					)}. ${
						dayButton.notification
							? I18nAndL10n.translate('Component.WeeklyCalendar.DayNotificationAvailableAppointments', {
									count: dayButton.notification.toString(),
								})
							: I18nAndL10n.translate('Component.WeeklyCalendar.DayNotificationNoAppointments')
					}`;

					return (
						<elements.day
							key={dayButton.date.date()}
							type='button'
							disabled={dayButton.disabled}
							aria-current={dayButton.date.isSame(currentDate, 'day')}
							onClick={() => handleDayClick(dayButton.date)}
							aria-label={ariaLabel}
						>
							<elements.dayName>{dayButton.date.format('ddd')}</elements.dayName>
							<elements.dayNumber>{dayButton.date.format('DD')}</elements.dayNumber>

							<elements.dayNotification>
								{dayButton.notification ? <div className='day-notification-dot' /> : null}
							</elements.dayNotification>
						</elements.day>
					);
				})}
			</elements.days>
		</elements.container>
	);
}

const elements = {
	container: styled.div`
		overflow: hidden;

		&:focus-visible {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 2px;
			border-radius: ${tokens.borderRadius.small};
		}
	`,
	currentDateContainer: styled.div`
		display: flex;
		justify-content: space-between;
		align-items: center;
		background: ${primitiveColorTokens.primary[300]};
		padding: ${tokens.spacing.x0_5};
		border-top-left-radius: ${tokens.borderRadius.small};
		border-top-right-radius: ${tokens.borderRadius.small};
	`,
	currentDate: styled.div`
		text-align: center;
	`,
	days: styled.div`
		background: ${tokens.color.bg.surfaceBrand};
		border-bottom-left-radius: ${tokens.borderRadius.small};
		border-bottom-right-radius: ${tokens.borderRadius.small};
		display: flex;
		justify-content: stretch;
		align-items: stretch;
		padding: ${tokens.spacing.x0_5};
		gap: ${tokens.spacing.x0_5};
	`,
	day: styled(HeadlessButton)`
		all: unset;
		width: 48px;
		flex-grow: 1;
		border-radius: ${tokens.borderRadius.small};
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		cursor: default;
		padding: ${tokens.spacing.x1};
		color: ${tokens.color.content.interactive};
		gap: 6px;

		&:not([aria-current='true'], [aria-disabled='true']) {
			@media (hover: hover) {
				&:hover {
					background: ${tokens.color.bg.interactiveMutedHover};
				}
			}

			&:active {
				background: ${tokens.color.bg.interactiveMutedActive};
			}
		}

		&[aria-current='true'] {
			background: ${tokens.color.bg.surfaceDarkBrand};
			color: ${tokens.color.content.onDark};

			.day-notification-dot {
				background: ${tokens.color.bg.highlight};
			}
		}

		&[aria-disabled='true'] {
			opacity: 0.7;
		}

		&[data-focus='true'] {
			outline: 2px solid ${tokens.color.focus};
			outline-offset: 1px;
		}

		.day-notification-dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			background: ${tokens.color.content.highlight};
		}
	`,
	dayName: styled.div`
		font: ${tokens.text.label.xSmallSemiBold};
		height: 16px;
		text-transform: capitalize;
	`,
	dayNumber: styled.div`
		font: ${tokens.text.label.largeSemiBold};
		height: 16px;
	`,
	dayNotification: styled.div`
		display: flex;
		align-items: center;
		height: 20px;
	`,
};
